import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "./AuthContext";
import { getAgreementSyncOutstandingCount } from "./services/agreementMatchDataService";

// Holds the combined "awaiting mapping + awaiting approval" count for the Agreement Sync nav
// badge. It lives above the router (HomeLayout) because the badge is in the Sidebar, which
// renders on every screen, while the thing that CLEARS the count is the Agreement Sync grid
// several levels down under the Outlet. Neither can reach the other without this.
const AgreementSyncCountContext = createContext({
  outstandingCount: 0,
  refreshOutstandingCount: () => {},
  setBadgeDealerId: () => {},
});

// Which field on the stored dealer object is the id the agreementsync routes want. Admins and
// dealers disagree, and the disagreement is load-bearing: AgreementsSyncHome.jsx:170, :183 and
// :196 each pass `dealerId` for an admin and `id` for a dealer to the very grids this badge
// counts. Getting this wrong does not error, it silently counts a DIFFERENT dealer.
const dealerIdFor = (dealer, group) => {
  if (!dealer) return null;
  return group === "jbw-admin" ? dealer.dealerId : dealer.id;
};

const storedDealer = () => {
  try {
    return JSON.parse(localStorage.getItem("dealer"));
  } catch {
    // A malformed entry must not take the whole sidebar down with it.
    return null;
  }
};

export function AgreementSyncCountProvider({ children }) {
  const { dealer, group } = useContext(AuthContext);
  const [outstandingCount, setOutstandingCount] = useState(0);

  // The dealer the badge is counting for. Seeded from AuthContext (set by the TopBar dealer
  // search and at sign-in) with a localStorage fallback, because AuthContext.dealer is state:
  // it is empty after a page reload even though localStorage still holds the selection.
  //
  // It is also settable, and has to be. The dealer search ON the Agreement Sync screen is
  // `type="syncSearch"`, and DealerSearch.jsx:62 writes neither localStorage nor setDealer for
  // that type -- so an admin switching dealers there is invisible here unless that screen says
  // so. AgreementsSyncHome calls setBadgeDealerId with whatever it hands the grid.
  const [badgeDealerId, setBadgeDealerId] = useState(null);

  useEffect(() => {
    const contextId = dealerIdFor(dealer, group);
    const id = contextId != null ? contextId : dealerIdFor(storedDealer(), group);
    // Only follow the ambient dealer while it actually resolves. Otherwise a re-render with no
    // dealer in AuthContext yet would clobber a selection the sync screen just made.
    if (id != null) setBadgeDealerId(id);
  }, [dealer, group]);

  const refreshOutstandingCount = useCallback(async () => {
    // No dealer selected yet (an admin who has not picked one) means nothing to count. Zero,
    // not stale: the badge must not keep showing the previous dealer's number.
    if (badgeDealerId == null || !group) {
      setOutstandingCount(0);
      return;
    }
    try {
      const res = await getAgreementSyncOutstandingCount(badgeDealerId);
      setOutstandingCount(Number(res?.data?.totalCount) || 0);
    } catch (err) {
      // A failed count is not worth an error surface in the nav chrome. Fall back to no badge
      // rather than freezing a number that may already be wrong.
      console.error("unable to load agreement sync outstanding count", err);
      setOutstandingCount(0);
    }
  }, [badgeDealerId, group]);

  useEffect(() => {
    refreshOutstandingCount();
  }, [refreshOutstandingCount]);

  const value = useMemo(
    () => ({ outstandingCount, refreshOutstandingCount, setBadgeDealerId }),
    [outstandingCount, refreshOutstandingCount]
  );

  return (
    <AgreementSyncCountContext.Provider value={value}>
      {children}
    </AgreementSyncCountContext.Provider>
  );
}

export const useAgreementSyncCount = () => useContext(AgreementSyncCountContext);

export { AgreementSyncCountContext };
